import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Link } from "react-router";
import {
  CircleCheck,
  Loader2,
  Mail,
  MapPin,
  MessageCircle,
  Phone,
  TriangleAlert,
} from "lucide-react";
import {
  SERVICE_INTERESTS,
  SERVICE_INTEREST_LABELS,
  createContactMessageSchema,
  type ContactFormValues,
  type CreateContactMessageInput,
  type LeadSubmissionResponse,
} from "@veo/types";
import { ApiError, fetchApi } from "../lib/api";
import {
  FormField,
  HoneypotField,
  SelectInput,
  TextArea,
  TextInput,
} from "../components/FormControls";
import { Reveal } from "../components/motion";
import { settingList, settingString, useSite } from "../lib/site";
import { SectionList } from "../blocks/registry";
import { loadOptionalPage, pageMeta } from "../lib/page";
import { seoMeta } from "../lib/seo";
import { loadSectionData } from "../lib/section-data";
import type { Route } from "./+types/contact";

export async function loader({ request }: Route.LoaderArgs) {
  const preview = new URL(request.url).searchParams.get("preview");
  const page = await loadOptionalPage("/contact", preview);

  return { page, sectionData: page ? await loadSectionData(page.sections) : {} };
}

export function meta({ loaderData, matches }: Route.MetaArgs) {
  if (loaderData?.page) return pageMeta(loaderData.page, { matches });

  return seoMeta({
    matches,
    title: "Contact — VEO Consultancy",
    description: "Get in touch with VEO Consultancy. We reply within one business day.",
    path: "/contact",
  });
}

export default function Contact({ loaderData }: Route.ComponentProps) {
  const { page, sectionData } = loaderData;

  const { settings } = useSite();
  const phone = settingString(settings, "contact.phone");
  const whatsapp = settingString(settings, "contact.whatsapp");
  const email = settingString(settings, "contact.email");
  const address = [
    settingString(settings, "contact.addressLine1"),
    settingString(settings, "contact.addressLine2"),
    settingString(settings, "contact.city"),
    settingString(settings, "contact.country"),
  ]
    // Lolgorien is currently both addressLine1 and city, so the same string
    // would otherwise print twice in one line.
    .filter((part, index, all) => part && all.indexOf(part) === index);
  const officeHours = settingList<{ days: string; hours: string }>(
    settings,
    "contact.officeHours",
  );

  const [submitted, setSubmitted] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const successRef = useRef<HTMLDivElement>(null);
  /**
   * Captured once, on first render, via a lazy state initialiser.
   *
   * `useRef(Date.now())` would evaluate the timestamp on every render — the
   * value is discarded after the first, but calling it during render is
   * impure and the React Compiler rightly refuses to optimise around it.
   */
  const [formLoadedAt] = useState(() => Date.now());

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ContactFormValues, unknown, CreateContactMessageInput>({
    resolver: zodResolver(createContactMessageSchema),
    defaultValues: {
      name: "",
      email: "",
      phone: "",
      serviceInterest: "GENERAL",
      message: "",
      website: "",
    },
  });

  useEffect(() => {
    if (submitted) successRef.current?.focus();
  }, [submitted]);

  const onSubmit = handleSubmit(async (values) => {
    setFormError(null);
    try {
      await fetchApi<LeadSubmissionResponse>("/public/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...values, formLoadedAt }),
      });
      setSubmitted(true);
    } catch (error) {
      if (error instanceof ApiError && error.status === 429) {
        setFormError(
          `You have sent several messages recently. Please wait a little while${
            phone ? `, or call us on ${phone}` : ""
          }.`,
        );
      } else {
        setFormError(
          `We could not send your message just now. Please try again${
            email ? `, or email ${email} directly` : ""
          }.`,
        );
      }
    }
  });

  // Placed after every hook, not before: an early return above them would
  // change the hook count between the two layouts and break the rules of hooks.
  if (page) {
    return (
      <main id="main">
        <SectionList sections={page.sections} data={sectionData} />
      </main>
    );
  }

  return (
    <main id="main">
      <section className="bg-navy-900 textured-dark text-cream-100">
        <div className="container-site py-16">
          <div className="container-content">
            <p className="eyebrow text-gold-300">Get in touch</p>
            <h1 className="mt-4 text-5xl text-cream-50">Contact us</h1>
            <p className="mt-5 text-lg leading-relaxed text-navy-100">
              Questions about our services, or anything else. We reply within one business day.
            </p>
          </div>
        </div>
      </section>

      <section className="bg-surface-0 textured">
        <div className="container-site section-y">
          <div className="grid gap-12 lg:grid-cols-[1fr_1.4fr]">
            {/* Contact details -------------------------------------- */}
            <Reveal>
              <h2 className="text-2xl">Speak to us directly</h2>

              {/*
                Every value here comes from Settings, the same source the footer
                reads. It used to be typed into this file, which is why the page
                still advertised +254 700 000 000 and info@ long after both had
                been corrected in the CMS — and why the footer and the page body
                disagreed with each other on the same screen.

                Each row is conditional: an unset detail is omitted rather than
                shown blank beside its icon.
              */}
              <dl className="mt-6 space-y-5">
                {phone && (
                  <div className="flex gap-3">
                    <Phone aria-hidden className="mt-0.5 size-5 shrink-0 text-gold-600" />
                    <div>
                      <dt className="text-xs uppercase tracking-wider text-ink-500">
                        {whatsapp && whatsapp === phone ? "Phone / WhatsApp" : "Phone"}
                      </dt>
                      <dd className="mt-0.5">
                        <a
                          href={`tel:${phone.replace(/[^\d+]/g, "")}`}
                          className="text-navy-900 hover:underline"
                        >
                          {phone}
                        </a>
                      </dd>
                    </div>
                  </div>
                )}

                {whatsapp && whatsapp !== phone && (
                  <div className="flex gap-3">
                    <MessageCircle aria-hidden className="mt-0.5 size-5 shrink-0 text-gold-600" />
                    <div>
                      <dt className="text-xs uppercase tracking-wider text-ink-500">WhatsApp</dt>
                      <dd className="mt-0.5">
                        <a
                          href={`https://wa.me/${whatsapp.replace(/[^\d]/g, "")}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-navy-900 hover:underline"
                        >
                          {whatsapp}
                        </a>
                      </dd>
                    </div>
                  </div>
                )}

                {email && (
                  <div className="flex min-w-0 gap-3">
                    <Mail aria-hidden className="mt-0.5 size-5 shrink-0 text-gold-600" />
                    <div className="min-w-0">
                      <dt className="text-xs uppercase tracking-wider text-ink-500">Email</dt>
                      <dd className="mt-0.5">
                        {/* Same treatment as the footer address, and for the
                            same reason — see `EmailText` in SiteChrome. */}
                        <a
                          href={`mailto:${email}`}
                          className="wrap-anywhere text-navy-900 hover:underline"
                        >
                          {email}
                        </a>
                      </dd>
                    </div>
                  </div>
                )}

                {address.length > 0 && (
                  <div className="flex gap-3">
                    <MapPin aria-hidden className="mt-0.5 size-5 shrink-0 text-gold-600" />
                    <div>
                      <dt className="text-xs uppercase tracking-wider text-ink-500">Office</dt>
                      <dd className="mt-0.5 text-navy-900">{address.join(", ")}</dd>
                    </div>
                  </div>
                )}
              </dl>

              {officeHours.length > 0 && (
                <div className="mt-8 rounded-lg bg-cream-200 p-6">
                  <h3 className="font-display text-lg text-navy-900">Office hours</h3>
                  <dl className="mt-3 space-y-1.5 text-sm text-ink-600">
                    {officeHours.map((entry) => (
                      <div key={entry.days} className="flex justify-between gap-4">
                        <dt>{entry.days}</dt>
                        <dd>{entry.hours}</dd>
                      </div>
                    ))}
                  </dl>
                </div>
              )}

              <p className="mt-6 text-sm leading-relaxed text-ink-600">
                Ready to talk properly?{" "}
                <Link
                  to="/book-consultation"
                  className="font-medium text-navy-700 underline decoration-gold-500 underline-offset-2"
                >
                  Book a consultation
                </Link>{" "}
                instead — it takes an hour and costs nothing.
              </p>
            </Reveal>

            {/* Form -------------------------------------------------- */}
            <Reveal delay={0.1}>
              {submitted ? (
                <div
                  ref={successRef}
                  tabIndex={-1}
                  role="status"
                  className="rounded-xl border border-growth-200 bg-growth-50/40 p-10 text-center focus:outline-none"
                >
                  <span className="mx-auto flex size-14 items-center justify-center rounded-full bg-white">
                    <CircleCheck aria-hidden className="size-7 text-growth-600" />
                  </span>
                  <h2 className="mt-6 text-2xl">Message sent</h2>
                  <p className="mt-3 leading-relaxed text-ink-600">
                    Thank you. We have emailed you a copy and will reply within one business day.
                  </p>
                  <Link
                    to="/insights"
                    className="mt-7 inline-block rounded-md bg-navy-900 px-6 py-3 text-sm font-semibold text-cream-50 hover:bg-navy-800"
                  >
                    Read our insights
                  </Link>
                </div>
              ) : (
                <form
                  onSubmit={onSubmit}
                  noValidate
                  className="relative space-y-6 rounded-xl bg-cream-100 p-7 md:p-9"
                >
                  <HoneypotField register={register("website")} />

                  {formError && (
                    <div
                      role="alert"
                      className="flex gap-3 rounded-md bg-danger-50 p-4 ring-1 ring-inset ring-danger-500/20"
                    >
                      <TriangleAlert aria-hidden className="size-5 shrink-0 text-danger-500" />
                      <p className="text-sm text-danger-700">{formError}</p>
                    </div>
                  )}

                  <FormField label="Name" htmlFor="name" required error={errors.name?.message}>
                    <TextInput
                      id="name"
                      autoComplete="name"
                      aria-invalid={Boolean(errors.name)}
                      aria-describedby={errors.name ? "name-error" : undefined}
                      {...register("name")}
                    />
                  </FormField>

                  <div className="grid gap-6 sm:grid-cols-2">
                    <FormField label="Email" htmlFor="email" required error={errors.email?.message}>
                      <TextInput
                        id="email"
                        type="email"
                        autoComplete="email"
                        aria-invalid={Boolean(errors.email)}
                        aria-describedby={errors.email ? "email-error" : undefined}
                        {...register("email")}
                      />
                    </FormField>

                    <FormField label="Phone" htmlFor="phone" error={errors.phone?.message}>
                      <TextInput
                        id="phone"
                        type="tel"
                        autoComplete="tel"
                        aria-invalid={Boolean(errors.phone)}
                        {...register("phone")}
                      />
                    </FormField>
                  </div>

                  <FormField
                    label="Service of interest"
                    htmlFor="contact-service"
                    required
                    error={errors.serviceInterest?.message}
                  >
                    <SelectInput id="contact-service" {...register("serviceInterest")}>
                      {SERVICE_INTERESTS.map((interest) => (
                        <option key={interest} value={interest}>
                          {SERVICE_INTEREST_LABELS[interest]}
                        </option>
                      ))}
                    </SelectInput>
                  </FormField>

                  <FormField
                    label="Message"
                    htmlFor="contact-message"
                    required
                    error={errors.message?.message}
                  >
                    <TextArea
                      id="contact-message"
                      aria-invalid={Boolean(errors.message)}
                      aria-describedby={errors.message ? "contact-message-error" : undefined}
                      {...register("message")}
                    />
                  </FormField>

                  <div className="rounded-md bg-white p-4">
                    <label className="flex gap-3">
                      <input
                        type="checkbox"
                        className="mt-1 size-4 shrink-0 rounded border-ink-400 text-navy-900 focus:ring-2 focus:ring-navy-700"
                        aria-invalid={Boolean(errors.consent)}
                        {...register("consent")}
                      />
                      <span className="text-sm leading-relaxed text-ink-700">
                        I consent to VEO Consultancy storing and using these details in order to
                        respond to my enquiry, as set out in the{" "}
                        <Link to="/privacy-policy" className="font-medium text-navy-700 underline">
                          Privacy Policy
                        </Link>
                        .
                        <span className="ml-1 text-danger-500" aria-hidden>
                          *
                        </span>
                      </span>
                    </label>
                    {errors.consent && (
                      <p role="alert" className="mt-2 text-xs font-medium text-danger-500">
                        {errors.consent.message}
                      </p>
                    )}
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    aria-busy={isSubmitting}
                    className="flex w-full items-center justify-center gap-2 rounded-md bg-navy-900 px-6 py-4 text-base font-semibold text-cream-50 transition-colors hover:bg-navy-800 disabled:cursor-not-allowed disabled:bg-navy-900/50"
                  >
                    {isSubmitting && <Loader2 aria-hidden className="size-4 animate-spin" />}
                    {isSubmitting ? "Sending…" : "Send message"}
                  </button>
                </form>
              )}
            </Reveal>
          </div>
        </div>
      </section>
    </main>
  );
}
